/**
 * ONE definition of "this crash is a failed dynamic import".
 *
 * Two boundaries have to agree on this question, and they are not free to
 * disagree, because they are two halves of one recovery:
 *
 *   - `SectionBoundary` answers YES by giving up its own Retry well and
 *     RETHROWING, on the stated promise that the boundary above can actually
 *     recover (React caches a `lazy()` loader's rejection, so a local retry
 *     re-throws the same rejection forever).
 *   - `ErrorBoundaryNext` answers YES by doing the one thing that works: a
 *     single hard reload against the new index.html.
 *
 * So a message the section boundary calls a chunk error and the app boundary
 * does not is strictly worse than no rethrow at all: the reader loses the
 * section-level notice AND gets no reload. That is not hypothetical. From
 * 2026-08-14 (`e6346c39`) until this module existed, the two predicates were
 * separate copies and had drifted by exactly one pattern, Safari's wording,
 * so on WebKit the handoff broke precisely where it was designed to work.
 *
 * Hence one exported predicate and no local copies. `chunkError.test.ts`
 * holds every shipped boundary to it by reading the sources.
 */

/**
 * Every wording a browser uses for a dynamic import that did not load.
 *
 * The engines word this differently and none of it is standardised, so the
 * set is a union over what the shipping engines actually say:
 *   - Chromium: "Failed to fetch dynamically imported module: <url>"
 *   - Firefox:  "error loading dynamically imported module: <url>"
 *   - WebKit:   "Importing a module script failed."
 *   - Bundler-generated (webpack-era, still emitted by some tooling):
 *     `ChunkLoadError` as a name, or "Loading chunk 42 failed".
 */
const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
  /Loading chunk [\d]+ failed/i,
  /dynamically imported module/i,
  /Failed to fetch dynamically imported/i,
  /Importing a module script failed/i,
];

/** True when `e` is a chunk/dynamic-import load failure rather than a real bug. */
export function isChunkLoadError(e: Error): boolean {
  if (e.name === "ChunkLoadError") return true;
  const message = e.message ?? "";
  return CHUNK_ERROR_PATTERNS.some((p) => p.test(message));
}
